import React, { useEffect, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useThirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook'
import { URLS } from '../../../resources/constants'

// Defensive: older job rows may still carry the internal artifact path in the description
// (newer backups already exclude it server-side). Never show it to the user.
const stripArtifact = (text: string) =>
    (text || '').replace(/\s*Artifact:.*$/i, '').trim()

// Shows QBO backup progress. There's no start button here: the backup runs automatically after a QBO import finishes. 
// We just find the latest job for the branch and poll it, showing an Alert for in_progress / failed / completed (with a Retry button on failure).
// Note: gl-api wraps responses, so the payload is at resp.data.data.

interface Props {
    branchId: number;
    onStatusChange?: (status: string) => void;
}

const POLL_MS = 5000

const QboBackupStatus: React.FC<Props> = ({ branchId, onStatusChange }) => {
    const { t } = useTranslation("scenes.thirdparty.quickbook.Dashboard")
    const { qbFetchBackupLatest, qbRetryBackup } = useThirdPartyApi()
    const [job, setJob] = useState<any>(null)
    const [apiError, setApiError] = useState<string | null>(null)
    const [retrying, setRetrying] = useState<boolean>(false)
    const interval = useRef<any>(null)

    useEffect(() => {
        if (!onStatusChange) return;
        if (!job) {
            onStatusChange(apiError ? 'failed' : 'ready');
        } else {
            onStatusChange(job.status || 'ready');
        }
    }, [job, apiError]);

    // Discover + poll the latest backup job for this branch. /latest already returns the
    // full status payload, so it doubles as the progress poll. Stops once terminal.
    useEffect(() => {
        if (!branchId) return

        const tick = () => {
            qbFetchBackupLatest({ branch_id: branchId }, (_msg: string, resp: any) => {
                const data = resp?.data?.data
                setApiError(null)
                if (!data || data.status === 'none' || !data.job_id) {
                    setJob(null)
                    clearInterval(interval.current)
                    return
                }
                setJob(data)
                if (data.status === 'completed' || data.status === 'failed') {
                    clearInterval(interval.current)
                }
            }, (msg: string, resp: any) => {
                const detail = resp?.data?.detail || resp?.data?.message || msg
                if (detail && typeof detail === 'string') {
                    setApiError(detail)
                }
            })
        }

        tick()
        interval.current = setInterval(tick, POLL_MS)
        return () => clearInterval(interval.current)
    }, [branchId])

    // Re-run the failed entities of the current job as a new job, then resume polling so the
    // freshly-created retry job (now the latest) shows up.
    const retry = () => {
        if (!job?.job_id || retrying) return
        setRetrying(true)
        qbRetryBackup({ job_id: job.job_id }, (_msg: string, resp: any) => {
            setRetrying(false)
            toast.success(resp?.data?.message || t('backup.retryStarted', 'Backup retry started'))
            qbFetchBackupLatest({ branch_id: branchId }, (_m: string, r: any) => {
                const data = r?.data?.data
                if (data && data.job_id) {
                    setJob(data)
                    clearInterval(interval.current)
                    interval.current = setInterval(() => {
                        qbFetchBackupLatest({ branch_id: branchId }, (_mm: string, rr: any) => {
                            const d = rr?.data?.data
                            if (!d || !d.job_id) return
                            setJob(d)
                            if (d.status === 'completed' || d.status === 'failed') clearInterval(interval.current)
                        }, () => {})
                    }, POLL_MS)
                }
            }, () => {})
        }, (_msg: string, resp: any) => {
            setRetrying(false)
            toast.error(resp?.data?.detail || resp?.data?.message || t('backup.retryFailed', 'Failed to retry backup'))
        })
    }

    const openDetails = () => {
        if (!job?.job_id) return;
        window.open(URLS.QBO_BACKUP_STATUS_PAGE.replace(':jobId', job.job_id), '_blank', 'noopener');
    };

    const chipBg: Record<string, string> = { 
        danger: '#f1aeb5', 
        success: '#a3cfbb', 
        primary: '#9ec5fe' 
    };
    const chipStyle: React.CSSProperties = {
        flex: 'none', 
        width: 36, 
        height: 36, 
        borderRadius: 10,
        display: 'inline-flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: 15,
    };

    if (!job) {
        if (apiError) {
            return (
                <Alert variant="warning" className="mb-0 d-flex align-items-center gap-3">
                    <span style={{ ...chipStyle, background: '#fff3cd' }}>
                        <i className="fas fa-exclamation-triangle text-warning" aria-hidden="true"></i>
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bold text-dark">{t('backup.pipeline.title', 'FreezerDoc Backup Pipeline')}</div>
                        <div className="text-dark small">{apiError}</div>
                    </div>
                    <span className="badge bg-warning text-dark px-3 py-2">
                        <i className="fas fa-info-circle me-1"></i> {t('backup.pipeline.setupNote', 'Setup Note')}
                    </span>
                </Alert>
            );
        }
        return (
            <Alert variant="secondary" className="mb-0 d-flex align-items-center gap-3">
                <span style={{ ...chipStyle, background: '#e2e8f0' }}>
                    <i className="fas fa-archive text-secondary" aria-hidden="true"></i>
                </span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-bold">{t('backup.pipeline.title', 'FreezerDoc Backup Pipeline')}</div>
                    <div className="text-muted small">{t('backup.pipeline.active', 'Automated FreezerDoc storage backup pipeline is active. Backups run automatically alongside QBO imports.')}</div>
                </div>
                <span className="badge bg-secondary px-3 py-2">
                    <i className="fas fa-shield-alt me-1"></i> {t('backup.pipeline.ready', 'FreezerDoc Ready')}
                </span>
            </Alert>
        );
    }

    const entities = job.entities || []
    const TERMINAL = ['completed', 'failed', 'skipped']
    const done = entities.filter((e: any) => e.status === 'completed').length
    const progress = entities.length ? ` (${done}/${entities.length})` : ''
    // Export is done once every entity is terminal but the job is still running, which means
    // we're now packaging + uploading to Freezer Doc (the long, progress-less tail phase).
    const savingToStorage = entities.length > 0 && entities.every((e: any) => TERMINAL.includes(e.status))
    const label = t('backup.label', 'QuickBooks backup')

    // View details stays inline in the message, in the alert's own bold color, not a
    // competing blue link.
    const detailsButton = (
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline ms-1"
            style={{ color: 'inherit', fontWeight: 700 }} onClick={openDetails}>
            {t('backup.viewDetails', 'View details')} <i className="fas fa-external-link-alt" aria-hidden="true"></i>
        </button>
    )

    const banner = (variant: string, iconClass: string, message: React.ReactNode, actions: React.ReactNode) => (
        <Alert variant={variant} className="mb-0 d-flex align-items-center gap-3">
            <span style={{ ...chipStyle, background: chipBg[variant] }}>
                <i className={iconClass} aria-hidden="true"></i>
            </span>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-bold">{label}</div>
                <div>{message}{detailsButton}</div>
            </div>
            {actions && <div className="d-flex align-items-center gap-2 flex-shrink-0">{actions}</div>}
        </Alert>
    )

    if (job.status === 'in_progress') {
        const message = savingToStorage
            ? t('backup.savingToStorage', 'Saving backup to storage…')
            : `${stripArtifact(job.description) || t('backup.inProgress', 'Backing up…')}${progress}`
        return banner('primary', 'fa fa-spinner fa-spin', message, null)
    }

    if (job.status === 'failed') {
        const message = stripArtifact(job.failure_message || job.description) || t('backup.failed', 'Backup failed')
        const hasAction = job.can_retry
        const actions = hasAction ? (
            <>
                {job.can_retry && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={retry} disabled={retrying}>
                        {retrying
                            ? <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i></>
                            : <><i className="fas fa-redo" aria-hidden="true"></i> {t('backup.retry', 'Retry')}</>}
                    </button>
                )}
            </>
        ) : null
        return banner('danger', 'fas fa-exclamation-triangle', message, actions)
    }

    // completed
    const message = `${stripArtifact(job.description) || t('backup.completed', 'Backup completed')}${progress}`
    return banner('success', 'fas fa-check-circle', message, null)
}

export default QboBackupStatus
